import { useState, useEffect, useCallback, useRef } from 'react'

interface Project {
  name: string
  path: string
  available: boolean
  repo_url?: string
  /** True project_name from spark.yaml, when it diverges from `name` (the
   * repo name) — e.g. after a repo-name collision. Callers checking
   * Temporal workflow activity must use this over `name`. */
  workflow_name?: string
}

interface ProjectsResponse {
  projects: Project[]
  selected: string | null
}

interface ProjectSelectorProps {
  onProjectChange?: (project: Project) => void
  getAuthHeaders?: () => Record<string, string>
  /** The project on screen. The server has one selected project for every
   * browser, so another session's switch changes what this one shows; the
   * selector follows it rather than naming a project no longer on screen. */
  currentName?: string
}

export function ProjectSelector({ onProjectChange, getAuthHeaders, currentName }: ProjectSelectorProps) {
  const [projects, setProjects] = useState<Project[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isChanging, setIsChanging] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  // Header passes a new onProjectChange on every render. Read it through a
  // ref so a new callback does not re-run the fetch: each re-run re-reported
  // the project, App cleared its Stop target to look it up again, and Stop
  // and Resume swapped on every render (2026-10-13).
  const onProjectChangeRef = useRef(onProjectChange)
  onProjectChangeRef.current = onProjectChange

  const fetchProjects = useCallback(async () => {
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {}
      const response = await fetch('/api/projects', { headers })
      if (!response.ok) {
        // Don't clear projects or stop loading on auth failure — will retry when token arrives
        if (response.status === 401) return
        console.error('Failed to fetch projects:', response.status)
        setIsLoading(false)
        return
      }
      const data: ProjectsResponse = await response.json()
      setProjects(data.projects)
      setSelected(data.selected)
      setIsLoading(false)
      // Report the initially selected project name
      const report = onProjectChangeRef.current
      if (data.selected && report) {
        const selectedProject = data.projects.find(p => p.path === data.selected)
        if (selectedProject) {
          report(selectedProject)
        }
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error)
      setIsLoading(false)
    }
  }, [getAuthHeaders])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  useEffect(() => {
    if (!currentName) return
    const shown = projects.find(p => p.name === currentName)
    if (shown) setSelected(shown.path)
  }, [currentName, projects])

  const handleChange = async (path: string) => {
    if (path === selected || isChanging) return

    setIsChanging(true)
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : { 'Content-Type': 'application/json' }
      const response = await fetch('/api/projects/select', {
        method: 'POST',
        headers,
        body: JSON.stringify({ path }),
      })

      const result = await response.json()
      if (result.success) {
        setSelected(path)
        const project = projects.find(p => p.path === path)
        if (project && onProjectChange) {
          onProjectChange(project)
        }
      } else {
        console.error('Failed to select project:', result.error)
      }
    } catch (error) {
      console.error('Failed to select project:', error)
    } finally {
      setIsChanging(false)
    }
  }

  if (isLoading) {
    return (
      <div className="text-gray-400 text-sm">Loading projects...</div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className="text-gray-500 text-sm italic">No projects found</div>
    )
  }

  const selectedProject = projects.find(p => p.path === selected)

  return (
    <div className="relative">
      {/* Mobile: custom dropdown to avoid ugly native <select> rendering */}
      <div className="lg:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isChanging}
          className="w-full flex items-center justify-between bg-cosmic-surface border border-cosmic-border rounded-lg px-4 py-3 text-gray-200 text-sm font-medium disabled:opacity-50 transition-colors"
        >
          <span>{selectedProject?.name || 'Select a project...'}</span>
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-cosmic-surface border border-cosmic-border rounded-lg overflow-hidden shadow-xl">
            {projects.map((project) => (
              <button
                key={project.path}
                onClick={() => { handleChange(project.path); setIsOpen(false); }}
                disabled={!project.available}
                className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                  project.path === selected
                    ? 'bg-cosmic-nebula/20 text-white font-medium'
                    : project.available
                      ? 'text-gray-300 hover:bg-white/5'
                      : 'text-gray-500 cursor-not-allowed'
                }`}
              >
                {project.name}{!project.available ? ' (unavailable)' : ''}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop: native select */}
      <div className="hidden lg:block relative">
        <select
          value={selected || ''}
          onChange={(e) => handleChange(e.target.value)}
          disabled={isChanging}
          className={`
            appearance-none bg-cosmic-surface border border-cosmic-border rounded-lg
            px-4 py-2 pr-8 text-gray-200 text-sm font-medium
            focus:outline-none focus:ring-2 focus:ring-cosmic-nebula focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            cursor-pointer hover:bg-cosmic-depth transition-colors
          `}
        >
          {!selected && (
            <option value="" disabled>
              Select a project...
            </option>
          )}
          {projects.map((project) => (
            <option
              key={project.path}
              value={project.path}
              disabled={!project.available}
              className={project.available ? '' : 'text-gray-500'}
            >
              {project.name}{!project.available ? ' (unavailable)' : ''}
            </option>
          ))}
        </select>

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isChanging && (
        <div className="absolute inset-0 flex items-center justify-center bg-cosmic-surface/80 rounded-lg">
          <div className="w-4 h-4 border-2 border-cosmic-nebula border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  )
}
